import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import ProductDetailModal from "../components/ProductDetailModal";
import EventDetailModal, { type EventLike } from "../components/EventDetailModal";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { useAuthStore } from "../store/authStore";
import { StatusIcon } from "../components/StatusIcon";
import { useT } from "../i18n";
import { useFmt } from "../i18n/fmt";
import { HitoBadge, ImpactFlags, MonitoringNote } from "../components/HitoBadge";



type Summary = {
  changes_today: number; changes_week: number;
  portfolio_changes: number; competitor_changes: number;   // 30 días
  portfolio_today: number; portfolio_week: number;         // panel 2×3 (R-15, §89)
  competitor_today: number; competitor_week: number;
  portfolio_items: number;                               // R-08: 0 → aviso «sin productos propios»
  portfolio_brands: number; competitor_brands: number;   // «N CN · M marcas» (§89)
  last_data_at: string | null;                           // fecha del último dato
  monitoring_since?: string | null;                      // «HelixScan monitoriza desde…» (28 sep)
  sources_status: Array<{ id: number; name: string; code: string; is_active: boolean; status: string; last_checked_at: string | null; last_successful_at: string | null; frequency: string; next_run: string | null }>;
  recent_changes: Array<EventLike & { product_name: string; active_ingredient: string; detected_at: string; relevance_score: number | null;
    first_competitor?: boolean; above_precio_menor?: boolean | null }>;
};

type DayActivity = { date: string; total: number; by_type: Record<string, number>; portfolio: number; competitor: number };

/** KPI en tarjeta de vidrio — número en tinta (v3 liquid glass). */
function ArrowUpRightIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 17 17 7M9 7h8v8" />
    </svg>
  );
}

function KPI({ label, value, sub, onClick }: { label: string; value: number; sub?: string; onClick?: () => void }) {
  const fmt = useFmt();
  const [hov, setHov] = useState(false);
  const active = hov && !!onClick;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: "relative",
        background: "var(--clr-surface)",
        border: `1px solid ${active ? "var(--clr-org-primary)" : "var(--clr-border)"}`,
        borderRadius: 18,
        padding: "1.15rem 1.25rem",
        backdropFilter: "var(--clr-blur)",
        WebkitBackdropFilter: "var(--clr-blur)",
        boxShadow: active ? "var(--clr-shadow)" : "var(--clr-shadow-sm)",
        transform: active ? "translateY(-2px)" : "none",
        transition: "transform 0.25s cubic-bezier(.25,.6,.3,1), box-shadow 0.25s, border-color 0.2s",
        cursor: onClick ? "pointer" : "default",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {/* Afordancia de "clicable": flecha ↗ arriba-derecha (como en el Dashboard/superadmin) */}
      {onClick && (
        <span style={{ position: "absolute", top: 14, right: 14, display: "flex", color: active ? "var(--clr-org-primary)" : "var(--clr-text4)", transition: "color 0.2s" }}>
          <ArrowUpRightIcon />
        </span>
      )}
      <div style={{ fontSize: "1.9rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", lineHeight: 1 }}>
        {fmt.num(value)}
      </div>
      <div style={{ fontSize: "0.8rem", fontWeight: 500, color: "var(--clr-text2)", marginTop: 9 }}>{label}</div>
      {sub && <div style={{ fontSize: "0.68rem", color: "var(--clr-text4)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function DashboardPage() {
  const t = useT();
  const fmt = useFmt();
  const G = useAuthStore(s => s.user?.primary_color) || "#0891b2";
  const TYPE_LABELS: Record<string, string> = {
    new_entry: t("dashboard.typeNewEntry"), price_change: t("dashboard.typePriceChange"),
    reimbursement_change: t("dashboard.typeReimbursementChange"), exclusion: t("dashboard.typeExclusion"),
    deduction: t("dashboard.typeDeduction"), warning: t("dashboard.typeWarning"),
    marketing_status: t("dashboard.typeMarketingStatus"),
    eu_registration: t("dashboard.typeEuRegistration"),
    regulation: t("dashboard.typeRegulation"),
    exclusivity: t("dashboard.typeExclusivity"),
  };
  const TYPE_COLORS: Record<string, string> = {
    new_entry: "#60a5fa", price_change: "#f59e0b",
    reimbursement_change: G, exclusion: "#f87171",
    deduction: "#a78bfa", warning: "#fb923c",
    marketing_status: "#f472b6",
    eu_registration: "#38bdf8",
    regulation: "#a78bfa",
    exclusivity: "#a855f7",
  };
  const [data, setData]         = useState<Summary | null>(null);
  const [activity, setActivity] = useState<DayActivity[]>([]);
  const [error, setError]       = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [detailCn, setDetailCn] = useState<string | null>(null);
  const [openEvent, setOpenEvent] = useState<EventLike | null>(null);
  const navigate = useNavigate();
  const { isMobile, isTablet } = useBreakpoint();

  useEffect(() => {
    api.get<Summary>("/dashboard/summary").then(setData).catch(e => setError(e.message));
    api.get<DayActivity[]>("/dashboard/activity").then(d => {
      const byDate: Record<string, DayActivity> = {};
      d.forEach(x => { byDate[x.date] = x; });
      const last3: DayActivity[] = [];
      for (let i = 0; i < 7; i++) {
        const day = new Date();
        day.setDate(day.getDate() - i);
        const key = day.toISOString().slice(0, 10);
        last3.push(byDate[key] ?? { date: key, total: 0, by_type: {}, portfolio: 0, competitor: 0 });
      }
      setActivity(last3);
    });
  }, [retryKey]);

  if (error) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 64, gap: 16, color: "var(--clr-text)" }}>
            <div style={{ fontSize: "1rem", fontWeight: 600, color: "#f87171" }}>{t("dashboard.errorTitle")}</div>
      <div style={{ fontSize: "0.82rem", color: "var(--clr-text3)", maxWidth: 400, textAlign: "center" }}>
        {t("dashboard.errorBody")}
      </div>
      <button
        onClick={() => { setError(""); setData(null); setRetryKey(k => k + 1); }}
        style={{ background: "#0891b2", color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", fontSize: "0.85rem", fontWeight: 600, cursor: "pointer", marginTop: 8 }}
      >
        {t("dashboard.retry")}
      </button>
    </div>
  );
  if (!data) return <div style={{ color: "var(--clr-text4)", padding: 32 }}>{t("dashboard.loading")}</div>;

  return (
    <div style={{ padding: isMobile ? "1rem" : "1.75rem 2rem", color: "var(--clr-text)" }}>
      <div style={{ marginBottom: "1.5rem" }}>
        <h1 style={{ margin: 0, fontSize: "1.3rem", fontWeight: 700 }}>{t("dashboard.pageTitle")}</h1>
        <div style={{ marginTop: 4 }}>
          <MonitoringNote since={data.monitoring_since} t={t} fmtDate={d => fmt.date(d)} />
        </div>
      </div>

      {/* R-08 (§90 Fase 2A): vigilar sin productos propios convierte todo en mercado. */}
      {data.portfolio_items === 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 18, padding: "10px 16px", borderRadius: 18,
          background: "var(--clr-surface)", border: "1px solid var(--clr-border)", boxShadow: "var(--clr-shadow-sm)" }}>
          <span aria-hidden style={{ width: 8, height: 8, borderRadius: 999, background: "#f59e0b", flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: "0.85rem", color: "var(--clr-text2)", minWidth: 200 }}>{t("dashboard.noPortfolioWarning")}</span>
          <button onClick={() => navigate("/portfolio")} style={{ background: "var(--clr-org-primary)", color: "#fff", border: 0, borderRadius: 999, padding: "6px 14px", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" }}>
            {t("dashboard.noPortfolioCta")}
          </button>
        </div>
      )}

      {/* Panel 2×3 (R-15/R-16, §89): Portfolio × Competencia × hoy / 7 d / 30 d.
          Antes había «Cambios hoy / esta semana» de TODO el universo de la org
          más dos tarjetas de 30 días; Carlos pidió leerlo por titularidad y
          horizonte. Cada tarjeta navega a Cambios con ese filtro preaplicado. */}
      {([
        { key: "portfolio", title: t("dashboard.rowPortfolio"), q: "is_portfolio=true",
          today: data.portfolio_today, week: data.portfolio_week, month: data.portfolio_changes, brands: data.portfolio_brands,
          navToday: t("dashboard.navPortfolioToday"), navWeek: t("dashboard.navPortfolioWeek"), navMonth: t("dashboard.navPortfolio") },
        { key: "competitor", title: t("dashboard.rowCompetitors"), q: "is_competitor=true",
          today: data.competitor_today, week: data.competitor_week, month: data.competitor_changes, brands: data.competitor_brands,
          navToday: t("dashboard.navCompetitorsToday"), navWeek: t("dashboard.navCompetitorsWeek"), navMonth: t("dashboard.navCompetitors") },
      ]).map(row => (
        <div key={row.key} style={{ marginBottom: 18 }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.9, color: "var(--clr-text4)", marginBottom: 8 }}>{row.title}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: isMobile ? 8 : 16 }}>
            <KPI label={t("dashboard.kpiTodayShort")} value={row.today} sub={t("dashboard.kpiTodaySub")}
              onClick={() => { const d = new Date().toISOString().slice(0,10); navigate(`/changes?date=${d}&${row.q}&label=${row.navToday}`); }} />
            <KPI label={t("dashboard.kpiWeekShort")} value={row.week} sub={t("dashboard.kpiWeekSub")}
              onClick={() => { const f = new Date(Date.now()-7*864e5).toISOString().slice(0,10); navigate(`/changes?from_date=${f}T00:00:00&${row.q}&label=${row.navWeek}`); }} />
            {/* «Número doble» (§89): el conteo se ancla al CN; junto a él, las marcas
                distintas, para que 18 CN no se lean como 18 productos. */}
            <KPI label={t("dashboard.kpiMonthShort")} value={row.month}
              sub={t("dashboard.brandsSub", { cn: row.month, brands: row.brands })}
              onClick={() => { const f = new Date(Date.now()-30*864e5).toISOString().slice(0,10); navigate(`/changes?from_date=${f}T00:00:00&${row.q}&label=${row.navMonth}`); }} />
          </div>
        </div>
      ))}
      {/* «Fecha del último dato» (§89): un cero puede ser «no ha pasado nada» o
          «todavía no han publicado»; sin esta fecha el usuario no lo distingue. */}
      <div style={{ marginTop: -8, marginBottom: 24, fontSize: "0.75rem", color: "var(--clr-text4)" }}>
        {data.last_data_at
          ? t("dashboard.lastDataAt", { date: fmt.dateTime(new Date(data.last_data_at), { dateStyle: "medium", timeStyle: "short" }) })
          : t("dashboard.lastDataNone")}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile || isTablet ? "1fr" : "1fr 320px", gap: 20 }}>
        {/* Recent changes */}
        <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 16, overflow: "hidden", backdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow)" }}>
          <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid var(--clr-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--clr-text2)" }}>{t("dashboard.recentChanges")}</span>
            <button onClick={() => navigate("/changes")} style={{ background: "transparent", border: "none", color: G, fontSize: "0.78rem", fontWeight: 600, cursor: "pointer", padding: "2px 0" }}>{t("dashboard.viewAll")}</button>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
              <thead>
                <tr style={{ color: "var(--clr-text3)", textAlign: "left" }}>
                  {[t("dashboard.colDate"),t("hitos.colHito"),...(isMobile?[]:[t("events.colBlock")]),t("dashboard.colProduct"),...(isMobile?[]:[t("dashboard.colActiveIngredient")])].map(h => (
                    <th key={h} style={{ padding: "8px 12px", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.recent_changes.length === 0 && (
                  <tr><td colSpan={isMobile ? 3 : 5} style={{ padding: 24, textAlign: "center", color: "var(--clr-text4)" }}>{t("dashboard.noChangesYet")}</td></tr>
                )}
                {data.recent_changes.map(c => (
                  <tr key={c.id} onClick={() => setOpenEvent(c)} title={t("events.openEvent")}
                    style={{ borderTop: "1px solid var(--clr-border2)", cursor: "pointer" }}
                    onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = "var(--clr-surface2)"}
                    onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = ""}>
                    <td style={{ padding: "8px 12px", color: "var(--clr-text3)", whiteSpace: "nowrap", fontSize: "0.75rem" }}>{c.detected_at ? fmt.date(new Date(c.detected_at), { timeZone: "UTC" }) : "–"}</td>
                    {/* R-17 (§89): fuera la columna «Prio.» (puntos rojos) — era la
                        titularidad con otro nombre y leía como semáforo. */}
                    <td style={{ padding: "8px 12px", whiteSpace: "nowrap", minWidth: 220 }}>
                      {/* Hito (§89/§90): sin hito, el tipo de siempre. Con severidad
                          otra vez (28 sep): ya es hito × relación con tu cartera,
                          no un atributo fijo. El nombre va entero: hay sitio. */}
                      <HitoBadge c={c} t={t} maxWidth={300}
                        fallback={<span style={{ background: TYPE_COLORS[c.change_type] + "22", color: TYPE_COLORS[c.change_type], padding: "2px 8px", borderRadius: 6, fontSize: "0.75rem", fontWeight: 600 }}>
                          {TYPE_LABELS[c.change_type] ?? c.change_type}
                        </span>} />
                      <ImpactFlags c={c} t={t} />
                    </td>
                    {/* Bloque del catálogo junto al hito (lo acordado con Carlos) */}
                    {!isMobile && <td style={{ padding: "8px 12px", color: "var(--clr-text3)", whiteSpace: "nowrap", fontSize: "0.76rem" }}>
                      {c.hito_group ? t(`hitos.group${c.hito_group}`) : "–"}
                    </td>}
                    <td style={{ padding: "4px 8px", maxWidth: 380, minWidth: 220 }}>
                      {c.cn_code && c.product_name ? (
                        <button onClick={e => { e.stopPropagation(); setDetailCn(c.cn_code); }}
                          title={t("dashboard.viewProductTooltip", { name: c.product_name })}
                          style={{
                            display: "flex", alignItems: "center", gap: 6, width: "100%",
                            background: "transparent", border: "1px solid transparent", borderRadius: 6,
                            padding: "4px 8px", cursor: "pointer",
                            color: c.is_portfolio ? G : "var(--clr-text)", fontSize: "inherit",
                            textAlign: "left", fontFamily: "inherit", fontWeight: c.is_portfolio ? 600 : 500,
                            transition: "background 0.15s ease, border-color 0.15s ease",
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = "var(--clr-surface2, rgba(255,255,255,0.5))";
                            e.currentTarget.style.borderColor = "var(--clr-border)";
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = "transparent";
                            e.currentTarget.style.borderColor = "transparent";
                          }}>
                          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {c.product_name}
                          </span>
                          <span style={{ fontSize: "0.78rem", color: "var(--clr-text4)", flexShrink: 0 }} aria-hidden>↗</span>
                        </button>
                      ) : (
                        <span style={{ padding: "4px 8px", color: "var(--clr-text4)" }}>{c.product_name || "–"}</span>
                      )}
                    </td>
                    {!isMobile && <td title={c.active_ingredient || undefined} style={{ padding: "8px 12px", color: "var(--clr-text2)", whiteSpace: "nowrap", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>{c.active_ingredient || "–"}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Activity preview */}
        <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 16, overflow: "hidden", backdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow)", marginBottom: 16 }}>
            <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid var(--clr-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--clr-text2)" }}>{t("dashboard.recentActivity")}</span>
              <button onClick={() => navigate("/history")} style={{ background: "transparent", border: "none", color: G, fontSize: "0.78rem", fontWeight: 600, cursor: "pointer", padding: "2px 0" }}>{t("dashboard.viewHistory")}</button>
            </div>
            <div style={{ padding: "0.5rem 0" }}>
              {activity.map(day => {
                const d = new Date(day.date + "T12:00:00");
                const today = new Date();
                const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
                const label = d.toDateString() === today.toDateString() ? t("dashboard.today")
                  : d.toDateString() === yesterday.toDateString() ? t("dashboard.yesterday")
                  : `${fmt.date(d, { weekday: "long" })}, ${d.getDate()} ${fmt.date(d, { month: "long" })} ${d.getFullYear()}`;
                const hasChanges = day.total > 0;
                return (
                  <div key={day.date} onClick={() => navigate(`/history`)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", borderBottom: "1px solid var(--clr-border2)", cursor: "pointer", opacity: hasChanges ? 1 : 0.5 }}
                    onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.background = "var(--clr-surface2)"}
                    onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.background = ""}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--clr-text)", textTransform: "capitalize" }}>{label}</div>
                      <div style={{ fontSize: "0.68rem", color: "var(--clr-text4)", marginTop: 1 }}>
                        {hasChanges
                          ? Object.entries(day.by_type).map(([type, n]) => `${n} ${TYPE_LABELS[type] ?? type}`).join(" · ")
                          : t("dashboard.noChanges")}
                      </div>
                    </div>
                    <span style={{ fontWeight: 800, fontSize: "1rem", color: hasChanges ? G : "var(--clr-text4)" }}>
                      {day.total}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

        {/* Sources status */}
        <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 16, overflow: "hidden", backdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow)" }}>
          <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid var(--clr-border)", fontSize: "0.85rem", fontWeight: 600, color: "var(--clr-text2)" }}>
            {t("dashboard.sourcesStatus")}
          </div>
          <div style={{ padding: "0.5rem 0" }}>
            {data.sources_status.map(s => {
              const dotColor = s.status === "ok" ? "#22c55e" : s.status === "warning" ? "#f59e0b" : s.status === "error" ? "#f87171" : "#94a3b8";
              const labelColor = dotColor;
              const label = s.status === "ok" ? t("dashboard.statusOk") : s.status === "warning" ? t("dashboard.statusWarning") : s.status === "error" ? t("dashboard.statusError") : s.status === "inactive" ? t("dashboard.statusInactive") : t("dashboard.statusPending");
              return (
                <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", borderBottom: "1px solid var(--clr-border2)", opacity: s.is_active ? 1 : 0.45 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: dotColor }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "0.8rem", fontWeight: 500, color: "var(--clr-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</div>
                    {!s.is_active ? (
                      <div style={{ fontSize: "0.7rem", color: "var(--clr-text4)" }}>{t("dashboard.sourceDisabled")}</div>
                    ) : (
                      <div style={{ fontSize: "0.68rem", color: "var(--clr-text4)", display: "flex", gap: 14, flexWrap: "wrap", marginTop: 2 }}>
                        <span>
                          <span style={{ color: "var(--clr-text4)", opacity: 0.7 }}>{t("dashboard.lastLabel")}</span>{" "}
                          {s.last_checked_at
                            ? fmt.dateTime(new Date(s.last_checked_at), { dateStyle: "short", timeStyle: "short" })
                            : "—"}
                        </span>
                        {s.next_run && (
                          <span>
                            <span style={{ color: "var(--clr-text4)", opacity: 0.7 }}>{t("dashboard.nextLabel")}</span>{" "}
                            {s.next_run}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: labelColor + "1e", color: labelColor,
                    borderRadius: 999, padding: "3px 11px",
                    fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0,
                  }}>
                    <StatusIcon status={s.status} size={9} color={dotColor} />
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {openEvent && <EventDetailModal event={openEvent} onClose={() => setOpenEvent(null)}
        onOpenProduct={cn => { setOpenEvent(null); setDetailCn(cn); }} />}
      {detailCn && <ProductDetailModal cn={detailCn} onClose={() => setDetailCn(null)} />}
    </div>
  );
}
